import { useMutation, useQuery } from 'react-query';

import { useSecurePortalFetcher } from '../../../../shared/hooks';
import { UseReactQueryOptions } from '../../../../shared/models';
import {
  CreateEditFolderResponse,
  CreateFolderRequest,
  EditFolderRequest,
  Folder,
  GetFolderContentResponse,
} from '../models';

export const useRootFoldersQuery = (
  options?: UseReactQueryOptions<Folder[]>
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['root-folders'],
    async () => {
      const { data } = await fetcher<{ folders: Folder[] }>(
        '/v1/folders'
      ).json();
      const { folders } = data;

      return folders;
    },
    options
  );
};

export const useFoldersTreeQuery = (
  options?: UseReactQueryOptions<Folder[]>
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['folders-tree'],
    async () => {
      const { data } = await fetcher<{ folders: Folder[] }>(
        '/v1/folders/tree'
      ).json();
      const { folders } = data;

      return folders;
    },
    options
  );
};

export const useFolderContentQuery = (
  folderId: string,
  options?: UseReactQueryOptions<GetFolderContentResponse>
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['folder-content', folderId],
    async () => {
      const { data } = await fetcher<GetFolderContentResponse>(
        `/v1/folders/${folderId}/content`
      ).json();

      return data;
    },
    options
  );
};

export const useEditFolder = () => {
  const fetcher = useSecurePortalFetcher();
  return useMutation<CreateEditFolderResponse, Error, EditFolderRequest>(
    async ({ name, folderId }: EditFolderRequest) => {
      const { data } = await fetcher<CreateEditFolderResponse>(
        `/v1/folders/${folderId}`,
        {
          method: 'PATCH',
          json: { name },
        }
      ).json();
      return data;
    }
  );
};

export const useCreateNewFolder = () => {
  const fetcher = useSecurePortalFetcher();
  return useMutation<CreateEditFolderResponse, Error, CreateFolderRequest>(
    async ({ name, parentId }: CreateFolderRequest) => {
      const { data } = await fetcher<CreateEditFolderResponse>('/v1/folders', {
        method: 'POST',
        json: { name, parentId },
      }).json();
      return data;
    }
  );
};

export const useMoveFolderMutation = () => {
  const fetcher = useSecurePortalFetcher();
  return useMutation(
    async ({
      sourceFolderId,
      destinationFolderId,
    }: {
      sourceFolderId: string;
      destinationFolderId: string;
    }) => {
      const { data } = await fetcher('/v1/folders/move', {
        method: 'PATCH',
        json: { sourceFolderId, destinationFolderId },
      }).json();
      return data;
    }
  );
};

export const useCopyFolderMutation = () => {
  const fetcher = useSecurePortalFetcher();
  return useMutation(
    async ({
      folderId,
      newFolderName,
    }: {
      folderId: string;
      newFolderName: string;
    }) => {
      const { data } = await fetcher<{ copyProcessId: string }>(
        `/v1/folders/${folderId}/copy`,
        { method: 'POST', json: { newFolderName } }
      ).json();
      return data;
    }
  );
};

export const useDeleteFolderMutation = () => {
  const fetcher = useSecurePortalFetcher();

  return useMutation(async (folderId: string) => {
    await fetcher(`/v1/folders/${folderId}`, {
      method: 'DELETE',
    }).flush();
  });
};
